import { Button, Flex, Icon, Skeleton, Text } from "@gravity-ui/uikit";
import { useAtom } from "@reatom/npm-react";
import cn from "classnames";
import {
  ComponentStateStatusEnum,
  StatusResponseOverallEnum,
} from "definitions/poizon-bff";
import moment from "moment";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { POIZON_API_SUPPORT_LINK } from "src/constants/links";
import { getServiceStatusAction } from "src/store/fetchers";

import { serviceCardInfo } from "./components/ServiceStatusCard/ServiceStatusCard.constants.tsx";
import { ServiceStatusCard } from "./components/ServiceStatusCard/ServiceStatusCard.tsx";
import { statusInfo } from "./ServiceStatusPage.constants.tsx";
import css from "./ServiceStatusPage.module.scss";

const PAGE_TITLE = "Статус сервисов";
const EMPTY_STATE_TEXT = "Сейчас нет данных о статусах сервисов.";
const INCIDENT_LINK_TEXT = "Следите за решением инцидента";

const formatUpdatedAt = (value: string): string => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("HH:mm, DD.MM.YYYY") : "";
};

export const ServiceStatusPage = () => {
  const [serviceStatus] = useAtom(getServiceStatusAction.dataAtom);
  const [isLoading] = useAtom(getServiceStatusAction.isLoadingAtom);

  const { overall, components, updated_at } = serviceStatus;

  const visibleComponents = useMemo(
    () =>
      components
        .filter((component) => serviceCardInfo[component.key])
        .sort(
          (a, b) => serviceCardInfo[a.key].order - serviceCardInfo[b.key].order,
        ),
    [components],
  );

  const operationalCount = visibleComponents.filter(
    (component) => component.status === ComponentStateStatusEnum.Operational,
  ).length;

  const isInitialLoading = isLoading && components.length === 0;
  const isEmpty = !isInitialLoading && visibleComponents.length === 0;

  const info = statusInfo[overall];
  const updatedAt = formatUpdatedAt(updated_at);
  const updatedLabel = updatedAt ? `Обновлено ${updatedAt}` : "";
  const hasIncident =
    overall === StatusResponseOverallEnum.Down ||
    overall === StatusResponseOverallEnum.Degraded;

  return (
    <div className={css.page}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>

      {isInitialLoading ? (
        <>
          <Skeleton className={css.bannerSkeleton} />
          <Skeleton className={css.cardSkeleton} />
          <Skeleton className={css.cardSkeleton} />
          <Skeleton className={css.cardSkeleton} />
        </>
      ) : (
        <>
          <div
            className={cn(css.banner, css[`banner_${overall.toLowerCase()}`])}
          >
            <Icon data={info.icon} size={36} className={css.bannerIcon} />
            <Flex direction="column" gap={1}>
              <Text variant="header-1">{info.bannerTitle}</Text>
              <Text color="secondary">
                {visibleComponents.length > 0
                  ? `Работают штатно: ${operationalCount} из ${visibleComponents.length}`
                  : "Нет отслеживаемых сервисов"}
              </Text>
              {updatedLabel && (
                <Text variant="caption-2" color="hint">
                  {updatedLabel}
                </Text>
              )}
              {hasIncident && (
                <Button
                  view="action"
                  size="m"
                  href={POIZON_API_SUPPORT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.incidentButton}
                >
                  {INCIDENT_LINK_TEXT}
                </Button>
              )}
            </Flex>
          </div>

          {isEmpty ? (
            <div className={css.empty}>
              <Text variant="body-2" color="secondary">
                {EMPTY_STATE_TEXT}
              </Text>
            </div>
          ) : (
            <Flex direction="column" gap={3}>
              {visibleComponents.map((component) => (
                <ServiceStatusCard key={component.key} state={component} />
              ))}
            </Flex>
          )}
        </>
      )}
    </div>
  );
};
